/**
 * Öffentliche Navigation (Paket S, 2026-09-10) — Disclosure-Muster.
 *
 * Die Menüs sind native <details>/<summary> und ohne Skript bedienbar. Dieses
 * Skript ergänzt nur: genau eine offene Gruppe, Hintergrundfläche, Escape mit
 * Fokusrückgabe, Klick außerhalb, Höhenbegrenzung auf den Sichtbereich und den
 * Platz des Modusschalters. Kein Netzwerk, kein Kontozustand.
 *
 * Schmaler Kopf (S-Nacharbeit, 2026-09-11): geschlossen stehen Marke links und
 * Menüauslöser rechts in EINER Zeile. Der Modusschalter steht davor, solange die
 * gemessene Zeile ihn trägt (auch bei Textvergrößerung); sonst wandert derselbe
 * Knopf mit sichtbarer Beschriftung in das geöffnete Menü vor die Sprachwahl.
 * Im Kopf bleibt es derselbe Knopf. Der zusätzliche Randknopf außerhalb des
 * Kopfes wird durch public-appearance.js gesteuert; beide teilen dieselbe Wahl.
 *
 * Sprachbereich (Redesign E, 2026-09-21): ein weiteres `.wa-nav-group` desselben Menübaums — Exklusivität,
 * Hintergrundfläche, Klick außerhalb, Fokus außerhalb und Schließen bei Linkklick gelten dadurch von selbst.
 * Ergänzt ist nur die innere Escape-Stufe: Escape schließt zuerst den Sprachbereich und gibt den Fokus an
 * seinen Auslöser zurück; erst ein weiteres Escape schließt das kompakte Hauptmenü. Links navigieren erst
 * bei Aktivierung; keine Pfeiltastenpflicht, keine Menürolle, kein Fokusfang. Dieses Skript baut keine
 * Adresse, liest und speichert keine Sprachwahl und löst keinen Abruf aus.
 * Geladen von `src/components/public/PublicHeader.astro` (defer).
 *
 * Menü in der Leiste beim Scrollen (Ownerentscheid 06.10.2026): Im kompakten Modus öffnet der Knopf der Leiste
 * (`[data-nav-dock-toggle]`) DIESELBE Menüfläche wie der Auslöser im Kopf – kein zweites Menü. Solange die Leiste
 * steht, hängt die Fläche (`.wa-nav-content`) in der Leiste direkt unter ihr, sonst wie immer im <details> des Kopfs;
 * der offene Zustand bleibt der des <details>. public-appearance.js meldet das Erscheinen und Verschwinden der Leiste
 * (`wa-reading-dock`), vorher bzw. nachher, damit Fokus und offene Fläche ohne Sprung mitwandern.
 */
(() => {
  "use strict";
  const root = document.documentElement;
  const header = document.querySelector('.wa-header');
  const shell = document.getElementById('public-navigation');
  const backdrop = document.querySelector('.wa-nav-backdrop');
  if (!header || !shell || !backdrop) return;
  const groups = [...shell.querySelectorAll('.wa-nav-group')];
  const trigger = shell.querySelector(':scope > summary');
  // Die gerenderten Bedienelemente bestimmen den Umbruch; die Breite gehört dem CSS.
  const compact = () => getComputedStyle(trigger).display !== 'none';
  let isCompact = compact();
  const appearance = header.querySelector('.wa-reading-switch');
  const inner = header.querySelector('.wa-header__inner');
  const controls = header.querySelector('.wa-header__controls');
  const brand = header.querySelector('.wa-brand');
  const content = shell.querySelector('.wa-nav-content');
  const dock = document.querySelector('[data-reading-dock]');
  const dockToggle = dock?.querySelector('[data-nav-dock-toggle]') ?? null;
  // Kopf und Leiste bilden zusammen den Bereich der Navigation (Escape, Fokus, Klick außerhalb).
  const zones = [header, dock].filter(Boolean);
  const inside = node => !!node && zones.some(zone => zone.contains(node));
  let inDock = false;
  // Hängt die Menüfläche in die Leiste oder zurück an ihren Platz im <details>; ein darin fokussiertes Element behält
  // den Fokus (Verschieben im DOM würde ihn sonst verlieren).
  const placeContent = next => {
    if (!content || !dock || next === inDock) return;
    const focused = content.contains(document.activeElement) ? document.activeElement : null;
    if (next) dock.append(content); else shell.append(content);
    inDock = next;
    header.dataset.navigationDocked = String(next);
    if (focused && document.activeElement !== focused) focused.focus({ preventScroll: true });
  };
  // Tatsächlicher Platzbedarf statt fester Breite: ragt die Bediengruppe über die Innenkante hinaus
  // (Marke auf Mindestbreite, Zeile ohne Umbruch), fehlt der Platz für den Schalter neben dem Auslöser.
  const overflows = () => {
    if (!inner || !controls) return false;
    const box = inner.getBoundingClientRect();
    const style = getComputedStyle(inner);
    const rightEdge = box.right - (parseFloat(style.paddingRight) || 0);
    const leftEdge = box.left + (parseFloat(style.paddingLeft) || 0);
    return controls.getBoundingClientRect().right > rightEdge + 0.5
      || (brand ? brand.getBoundingClientRect().left < leftEdge - 0.5 : false);
  };
  const positionAppearance = () => {
    if (!appearance) return;
    const focused = appearance.contains(document.activeElement) ? document.activeElement : null;
    appearance.classList.remove('wa-reading-switch--menu');
    if (isCompact) {
      // Mobil endet der Kopf mit dem Menü; der Schalter steht davor, damit die Fokusreihenfolge der Sichtreihenfolge folgt.
      if (appearance.nextElementSibling !== shell) shell.before(appearance);
      header.dataset.appearance = 'inline';
      if (overflows() && content) {
        // Kein Platz in der geschlossenen Zeile: derselbe Knopf, beschriftet, im geöffneten Menü vor der Sprachwahl.
        const lang = content.querySelector(':scope > .wa-language');
        if (lang) lang.before(appearance); else content.append(appearance);
        appearance.classList.add('wa-reading-switch--menu');
        header.dataset.appearance = 'menu';
      }
    } else {
      if (shell.nextElementSibling !== appearance) shell.after(appearance);
      header.dataset.appearance = 'inline';
    }
    if (focused && document.activeElement !== focused) focused.focus({ preventScroll: true });
  };
  const geometry = () => {
    // Aus der Leiste geöffnet beginnt die Hintergrundfläche unter der Leiste; die Leiste bleibt scharf und bedienbar.
    const bottom = (inDock ? dock : header).getBoundingClientRect().bottom;
    const panel = isCompact ? content : groups.find(group => group.open)?.querySelector('.wa-menu-panel');
    const anchor = panel?.offsetParent;
    const panelTop = anchor ? anchor.getBoundingClientRect().top + anchor.clientTop + panel.offsetTop : bottom;
    root.style.setProperty('--wa-nav-bottom', `${Math.max(0, bottom)}px`);
    root.style.setProperty('--wa-nav-available', `${Math.max(80, innerHeight - Math.max(0, panelTop) - 12)}px`);
  };
  // Der tatsächliche DOM-Zustand, nicht die (asynchron nachgezogene) Hintergrundfläche.
  const anyOpen = () => (isCompact ? shell.open : groups.some(group => group.open));
  const sync = () => {
    const open = anyOpen();
    backdrop.hidden = !open;
    header.dataset.navigationOpen = String(open);
    dockToggle?.setAttribute('aria-expanded', String(isCompact && shell.open));
    geometry();
  };
  const close = (focus = false) => {
    const active = groups.find(group => group.open);
    const fromDock = inDock;
    groups.forEach(group => { group.open = false; });
    if (isCompact) shell.open = false;
    placeContent(false);
    sync();
    // Aus der Leiste geöffnet kehrt der Fokus zu deren Knopf zurück, sonst zum Auslöser im Kopf.
    if (focus) (fromDock ? dockToggle : isCompact ? trigger : active?.querySelector('summary'))?.focus();
  };
  groups.forEach(group => group.addEventListener('toggle', () => {
    if (group.open) groups.filter(other => other !== group).forEach(other => { other.open = false; });
    sync();
  }));
  shell.addEventListener('toggle', event => {
    if (event.target !== shell) return;
    if (!shell.open) { groups.forEach(group => { group.open = false; }); placeContent(false); }
    sync();
  });
  // Knopf der Leiste: öffnet die Menüfläche direkt unter der Leiste bzw. schließt sie wieder.
  dockToggle?.addEventListener('click', () => {
    if (!isCompact) return;
    if (shell.open) { close(true); return; }
    placeContent(true);
    shell.open = true;
    sync();
  });
  // Die Leiste verschwindet (bevor sie verborgen wird): Eine aus der Leiste geöffnete Fläche geht offen an den Kopf zurück.
  // Ein im Kopf geöffnetes Menü bleibt dagegen im Kopf und damit in seiner Navigation, auch wenn die Leiste erscheint:
  // Das Umhängen nahm die Links aus dem Navigationsbereich (Vorlesewerkzeuge, Prüfung public-en bei 200 % Schrift).
  document.addEventListener('wa-reading-dock', event => {
    const docked = Boolean(event.detail && event.detail.docked);
    if (!docked) placeContent(false);
    sync();
  });
  // Innere Stufe vor der äußeren (Capture-Phase): nur wenn der Fokus im geöffneten Sprachbereich steht.
  // In der weiten Zeile ist das zugleich der ganze Vorgang; im kompakten Menü bleibt das Hauptmenü offen.
  const language = shell.querySelector('.wa-language');
  if (language) zones.forEach(zone => zone.addEventListener('keydown', event => {
    if (event.key !== 'Escape' || !language.open || !language.contains(event.target)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    language.open = false;
    sync();
    language.querySelector('summary')?.focus();
  }, true));
  zones.forEach(zone => zone.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    // Direkt am <details>-Zustand prüfen: das toggle-Ereignis (und damit sync) kommt asynchron nach.
    if (anyOpen()) { event.preventDefault(); close(true); }
  }));
  backdrop.addEventListener('click', () => close(true));
  document.addEventListener('pointerdown', event => {
    if (!inside(event.target) && event.target !== backdrop) close();
  });
  zones.forEach(zone => zone.addEventListener('focusout', event => {
    if (event.relatedTarget && event.relatedTarget !== backdrop && !inside(event.relatedTarget)) close();
  }));
  shell.querySelectorAll('a').forEach(link => link.addEventListener('click', () => close()));
  const resize = () => {
    const next = compact();
    if (next !== isCompact) {
      const focused = shell.contains(document.activeElement);
      close();
      isCompact = next;
      shell.open = !isCompact;
      positionAppearance();
      if (focused) (isCompact ? trigger : groups[0]?.querySelector('summary'))?.focus();
      sync();
    } else if (isCompact) {
      // Breite oder Schriftgröße haben sich geändert: Platz des Schalters neu messen.
      positionAppearance();
    }
    geometry();
  };
  // Der Observer läuft vor dem Zeichnen. DOM-Verschiebungen des Modusschalters
  // und Geometrievariablen dürfen seine laufende Größenlieferung nicht erneut
  // verändern (Firefox/WebKit: "ResizeObserver loop ..."). Eine gemeinsame
  // nächste Zeichenphase hält Breite, Schriftvergrößerung und Fokus synchron.
  let resizeFrame = 0;
  const queueResize = () => {
    if (resizeFrame) return;
    resizeFrame = requestAnimationFrame(() => { resizeFrame = 0; resize(); });
  };
  // Die Beobachtung abgebrochener View Transitions steht in public-appearance.js: Sie muss vor
  // `pagereveal` registriert sein, und dieses Skript läuft erst nach dem Parsen (defer).
  window.addEventListener('resize', queueResize, { passive: true });
  window.addEventListener('scroll', geometry, { passive: true });
  if ('ResizeObserver' in window) new ResizeObserver(queueResize).observe(header);
  shell.open = !isCompact;
  positionAppearance();
  sync();
})();
